import React, { cloneElement, Component, isValidElement, PropTypes } from 'react';
import { Dialog, FlatButton, Paper, TextField } from 'material-ui';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import Lock from 'material-ui/svg-icons/action/lock-outline';
import moment from 'moment';
import pluralize from 'pluralize';
import { pathOr } from 'ramda'

import PlanItems from './PlanItems';
import PlanSelector from './PlanSelector';
import PlanDetails from './PlanDetails';
import CreditCardForm from '../tc-ui/CreditCardForm';

import SectionHeading from '../tc-ui/section-heading';

import { getCoupon } from '../../stores/billing/billing-actions';

class Upgrade extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedPlanIndex: props.selectedPlanIndex || 0,
      isAnnualPlan: props.isAnnualPlan,
      showCreditCardForm: true, // switch to false to return to old upgrade page behavior
      showCouponDialog: props.showCouponDialog || false,
      couponCode: '',
      couponError: '',
    };
  }

  onPlanClick = (i) => {
    this.setState({ selectedPlanIndex: i });
  }

  onTermClick = () => {
    this.setState({ isAnnualPlan: !this.state.isAnnualPlan });
  }

  onUpgradeClick = () => {
    this.setState({ showCreditCardForm: true });
  }

  getDisplayPrice = (price, coupon) => {
    if (!coupon) return price;
    switch (coupon.discount_type) {
      case 'percentage':
        return (price - (coupon.value / 100) * price);
      case 'amount':
        const discountedPrice = price - coupon.value;
        return discountedPrice > 0 ? discountedPrice : 0;
      default:
        return price;
    }
  }

  filterPlan = (planTerm) => {
    const { isAnnualPlan } = this.state;
    return (isAnnualPlan ? 'year' : 'month') === planTerm;
  }

  handleCouponDialogState = () => {
    this.setState({
      couponCode: '',
      couponError: '',
      showCouponDialog: !this.state.showCouponDialog,
    });
  }

  handleCouponCodeChange = (e) => {
    this.setState({
      couponCode: e.target.value,
    });
  }

  handleCouponSubmit = () => {
    const { dispatch } = this.props;
    if (this.state.couponCode) {
      dispatch(getCoupon(this.state.couponCode))
        .then(this.handleCouponDialogState)
        .catch(() => {
          this.setState({ couponError: `"${this.state.couponCode}" is an invalid promo code. If you believe this is in error, please email support@tourconnect.com.` });
        });
    } else {
      this.setState({ couponError: 'Please enter a promo code.' });
    }
  }

  getUniquePlans = (plans) => {
    return plans.filter((plan, index, array) =>
      array.findIndex(t => t.name === plan.name) === index
    );
  }

  render() {
    const {
      props: {
        billing,
        billing: { coupon },
        header,
        isProcessing,
        onClose,
        profile,
        stripePublicKey,
        user,
      },
      state: {
        isAnnualPlan, selectedPlanIndex, showCouponDialog, couponError,
      }
    } = this;
    const companyType = user.user.type;
    const { productType } = user.company;
    const isSupplier = (companyType !== 'Contractor');
    const plans = [
      {
        name: 'Connected',
        planId: 'b2b_platform_monthly',
        price: this.getDisplayPrice(120, coupon),
        additional: this.getDisplayPrice(30, coupon),
        term: 'month',
        planItems: [
          'Unlimited Connection Requests',
          'Priority Listing in Search Results',
          'See Who has Viewed Your Profile',
          'Partner Match Notifications',
          !isSupplier ? 'Send Up To 200 Rates Requests' : 'Unlimited Marketing Materials',
        ],
      },
      {
        name: 'Connected',
        planId: 'b2b_platform_annual',
        price: this.getDisplayPrice(1200, coupon),
        additional: this.getDisplayPrice(300, coupon),
        term: 'year',
        planItems: [
          'Unlimited Connection Requests',
          'Priority Listing in Search Results',
          'See Who has Viewed Your Profile',
          'Partner Match Notifications',
          !isSupplier ? 'Send Up To 200 Rates Requests' : 'Unlimited Marketing Materials',
        ],
        ccDescription: {
          name: 'Connected Plan',
          type: 'Annual (12-months)',
          renewal: 'Automatic',
        },
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        planItems: [
          'Unlimited Connection Requests',
          'Priority Listing in Search Results',
          'See Who has Viewed Your Profile',
          'Partner Match Notifications',
          'Unlimited Contracts & Rates',
          'API Access & Integrations',
        ],
      },
    ];

    const selectedPlan = plans.filter(plan => !plan.term || this.filterPlan(plan.term))[!isSupplier ? selectedPlanIndex : 0];

    const marketingReadyLocations = pathOr([], ['locations'], profile).filter(location => location.isMarketingReady);
    const creditCardForm = () => {
      const supplierProductInfo = () => {
        if (marketingReadyLocations.length < 1) return undefined;
        return (
          <div style={{ display: 'inline-block', verticalAlign: 'top', width: '50%' }}>
            <SectionHeading
              title={`${marketingReadyLocations.length} Marketing Ready ${
                pluralize(
                  productType === 'Non-Accommodation' ? 'Location' : 'Property',
                  marketingReadyLocations.length
                )
              }`}
              style={{ whiteSpace: 'pre-wrap' }}
            />
            <div style={{ maxHeight: '100px', overflowY: 'scroll' }}>
              {
                marketingReadyLocations.map((location, i) =>
                  <div key={i} className="flex-tc-sb">
                    <div style={{ padding: '4px' }}>{`${location.name}`}</div>
                    <div style={{ padding: '4px' }}>{`$${i ? selectedPlan.additional : selectedPlan.price}`}</div>
                  </div>
                )
              }
            </div>
          </div>
        );
      };
      return (
        <div style={{ margin: '0 auto', maxWidth: '1232px', paddingTop: '50px' }}>
          <div style={{ width: '75%', margin: '0 auto' }}>
            { supplierProductInfo() }
            <div
              style={{
                display: 'inline-block',
                padding: '15px',
                verticalAlign: 'top',
                width: isSupplier && marketingReadyLocations.length ? '50%' : '100%',
                textAlign: 'center',
              }}
            >
              { marketingReadyLocations.length > 0 && (
                <p style={{ fontSize: '14px' }}>
                  {`Your subscription will be for ${marketingReadyLocations.length} marketing ready ${pluralize(
                  productType === 'Non-Accommodation' ? 'location' : 'property',
                  marketingReadyLocations.length
                )}
                  on the Connected Plan and will renew on ${moment().clone().add(1, selectedPlan.term).format('ll')}.`}
                </p>
              )}
              { (marketingReadyLocations.length === 0 || !isSupplier) && (
                <p style={{ fontSize: '14px' }}>
                  Your account will be upgraded to the Connected Plan and will renew on {moment().clone().add(1, selectedPlan.term).format('ll')}.
                </p>
              )}
            </div>
            {/* { selectedPlan.ccDescription && (
              <div>
                <div><span style={{ fontWeight: 'bold' }}>Upgrade to:</span> {selectedPlan.ccDescription.name}</div>
                <div><span style={{ fontWeight: 'bold' }}>Subscription Type</span> {selectedPlan.ccDescription.type}</div>
                <div><span style={{ fontWeight: 'bold' }}>Subscription Renewal</span> {selectedPlan.ccDescription.renewal}</div>
                <div><span style={{ fontWeight: 'bold' }}>Total Payment</span> ${(marketingReadyLocations.length || 1) * selectedPlan.price}USD</div>
              </div>
            )} */}
            <h4 style={{ textAlign: 'right' }}>
              Total Charged Today{
                isSupplier && (
                  ` (${
                    marketingReadyLocations.length || 1
                    } ${pluralize(
                      productType === 'Non-Accommodation' ? 'Location' : 'Property',
                      marketingReadyLocations.length
                    )})`
                )
              } - {selectedPlan.price + ((marketingReadyLocations.length || 1) - 1) * selectedPlan.additional}<span style={{ fontSize: '12px' }}>USD</span>
            </h4>
          </div>
          <Paper style={{ width: '75%', margin: '0 auto' }}>
            <div style={{ backgroundColor: 'rgb(0, 188, 212)', borderRadius: '2px 2px 0 0', padding: '10px', textAlign: 'left', width: '100%' }}>
              <Lock style={{ color: '#fff', width: '30px', height: '30px' }} />
            </div>
            <div style={{ padding: '25px' }}>
              { billing.errorMessage && <p
                  style={{
                    color: '#ff5e5c',
                    lineHeight: '18px',
                    marginBottom: '12px',
                    borderRadius: '4px',
                    background: '#fff5f5',
                    padding: '16px 23px 16px 16px',
                    fontSize: '12px',
                  }}
                >
                {billing.errorMessage}
              </p> }
              <CreditCardForm
                stripePublicKey={stripePublicKey}
                buttonStyle={{ display: 'block', margin: '40px auto 25px auto' }}
                containerStyle={{ width: '100%' }} inputFieldStyle={{ verticalAlign: 'top' }}
                submitDisabled={isProcessing}
                isProcessing={isProcessing}
                onCreateToken={(status, response) => {
                  if (status === 200) this.props.onUpgrade(selectedPlan, response);
                }}
              />
            </div>
          </Paper>
          {!coupon && (
            <div style={{ display: 'block', textAlign: 'right', margin: '1em auto', width: '75%' }}>
              <a href="" onClick={(e) => { e.preventDefault(); this.handleCouponDialogState(); }}>Promo Code?</a>
            </div>
          )}
        </div>
      );
    }

    const planDetails = () => (
      <div style={{ margin: '0 auto', maxWidth: '1232px' }}>
        { header && <div style={{ width: '75%', padding: '0', margin: '0 auto 25px auto' }}>
          { isValidElement(header) ? cloneElement(header) : <h1 style={{ fontSize: '21px', fontWeight: 300, lineHeight: '36px', margin: 0, textAlign: 'center', paddingTop: '25px' }}>{header}</h1> }
        </div>
        }
        {/* { !isSupplier && <PlanSelector plans={this.getUniquePlans(plans)} selectedPlanIndex={selectedPlanIndex} onPlanClick={this.onPlanClick} /> } */}
        <PlanDetails
          companyType={companyType}
          productType={productType}
          onTermClick={this.onTermClick}
          onUpgradeClick={this.onUpgradeClick}
          plan={selectedPlan}
          isAnnualPlan={isAnnualPlan}
        />
        {/* <div style={{ width: '75%', margin: '0 auto' }}>
          <PlanItems upgradeReason={upgradeReason} />
        </div> */}
      </div>
    );

    return (
      <div style={{ backgroundColor: 'rgb(250, 250, 250)', position: 'fixed', top: 0, left: 0, height: '100%', width: '100%', overflowY: 'scroll', zIndex: '100' }}>
        <CloseIcon onClick={onClose} style={{ position: 'absolute', right: '10px', top: '10px', height: '36px', width: '36px', cursor: 'pointer' }}/>
        {planDetails()}
        {creditCardForm()}
        <Dialog actions={[
          <FlatButton
            label="Cancel"
            primary
            onTouchTap={this.handleCouponDialogState}
          />,
          <FlatButton
            label="Add"
            secondary
            onTouchTap={this.handleCouponSubmit}
          />]}
          contentStyle={{ maxWidth: '500px' }}
          onRequestClose={this.handleCouponDialogState}
          open={showCouponDialog}
          title="Promo Code"
        >
        { couponError && <p style={{
          color: '#ff5e5c',
          lineHeight: '18px',
          marginBottom: '12px',
          borderRadius: '4px',
          background: '#fff5f5',
          padding: '16px 23px 16px 16px',
          fontSize: '12px',
        }}
        >
          {couponError}
        </p>}
          <TextField
            name="couponCode"
            fullWidth
            onChange={this.handleCouponCodeChange}
            hintText="Enter Promo Code"
          />
        </Dialog>
      </div>
    );
  }

}

Upgrade.propTypes = {
  children: PropTypes.object,
  dispatch: PropTypes.func,
  history: PropTypes.object,
  onClose: PropTypes.func,
  profile: PropTypes.object,
  user: PropTypes.object,
  header: PropTypes.node,
};

export default Upgrade;



// WEBPACK FOOTER //
// ./src/app/components/upgrade/index.jsx